'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useRouter } from 'next/router';

import { DeviceType } from '@/common/common.contants';
import { hasRNWebViewMessaging } from '@/utils/platform.util';

import { SnackbarProvider } from './SnackbarContext';

// ============================================
// RN WebView Message Types
// ============================================

/** Message type: url for navigation, action for app actions */
export enum RNMessageType {
  URL = 'URL',
  OPEN_IN_APP_BROWSER = 'OPEN_IN_APP_BROWSER',
  AUTH_SESSION_EXPIRED = 'AUTH_SESSION_EXPIRED',
  AUTH_TOKEN_REFRESHED = 'AUTH_TOKEN_REFRESHED',
}

type IncomingRNMessage = {
  type: RNMessageType.URL;
  data: { value: string };
};

type OutgoingRNMessage =
  | { type: RNMessageType.OPEN_IN_APP_BROWSER; data: { value: string } }
  | { type: RNMessageType.AUTH_SESSION_EXPIRED; data: Record<string, never> }
  | { type: RNMessageType.AUTH_TOKEN_REFRESHED; data: { sid: string } };

/** Callback type for incoming message handlers */
type RNMessageHandler = (message: IncomingRNMessage) => void;

// ============================================
// Message validation
// ============================================

const isValidRNMessage = (data: unknown): data is IncomingRNMessage => {
  if (!data || typeof data !== 'object') return false;
  const msg = data as Record<string, unknown>;
  if (msg.type !== RNMessageType.URL) return false;
  const msgData = msg.data as Record<string, unknown> | undefined;
  return (
    !!msgData &&
    typeof msgData === 'object' &&
    typeof msgData.value === 'string' &&
    msgData.value.length > 0
  );
};

// ============================================
// Cross-platform message data extraction
// ============================================

interface RNMessageEvent extends MessageEvent {
  nativeEvent?: { data?: string };
}

/**
 * Extracts message data from event, handling iOS/Android differences.
 * - Android: event.data (string)
 * - iOS: event.data or event.nativeEvent.data
 */
const extractMessageData = (event: RNMessageEvent): string | null => {
  if (event.data && typeof event.data === 'string') {
    return event.data;
  }
  if (event.nativeEvent?.data && typeof event.nativeEvent.data === 'string') {
    return event.nativeEvent.data;
  }
  if (event.data && typeof event.data === 'object') {
    try {
      return JSON.stringify(event.data);
    } catch {
      return null;
    }
  }
  return null;
};

// ============================================
// ReactNativeWebView type declaration
// ============================================

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

// ============================================
// URL Classification
// ============================================

type UrlClassification =
  | 'internal'
  | 'same-domain'
  | 'external'
  | 'passthrough';

/** Schemes that should be left to the browser / OS (phone, email, etc.) */
const PASSTHROUGH_SCHEMES = new Set([
  'tel:',
  'mailto:',
  'sms:',
  'geo:',
  'maps:',
]);

/** Schemes that must never be navigated to */
const BLOCKED_SCHEMES = new Set(['javascript:', 'data:', 'vbscript:', 'blob:']);

/** Optional config for testability — falls back to env vars when omitted */
interface ClassifyUrlConfig {
  appUrl?: string;
}

/**
 * Known second-level domain labels that form part of a two-part TLD.
 * e.g. "com" in ".com.my", "co" in ".co.uk", "org" in ".org.au"
 *
 * When the second-to-last label of a hostname matches one of these,
 * the TLD is treated as two parts, so the registrable domain is the
 * last 3 labels (e.g. "makro.com.my") instead of 2 (e.g. "com.my").
 */
const SECOND_LEVEL_TLDS = new Set([
  'com',
  'co',
  'net',
  'org',
  'gov',
  'edu',
  'ac',
  'mil',
  'gen',
  'biz',
  'info',
  'name',
  'pro',
  'aero',
  'int',
  'sch',
  'nhs',
]);

/**
 * Classify a URL relative to the app's origin.
 *
 * - "internal":    exact hostname match → Next.js router
 * - "same-domain": same base domain, different subdomain → window.location.href
 * - "external":    different domain entirely → RN in-app browser
 * - "passthrough": native scheme (tel:, mailto:) → browser/OS
 *
 * Base domain (registrable domain) is automatically extracted from
 * NEXT_PUBLIC_APP_URL. Handles multi-level subdomains and multi-part
 * TLDs (.com.my, .co.uk, .co.th, etc.) correctly.
 *
 * Example: appUrl = "https://storefront.my-dev.siammakro.cloud"
 *   → registrable domain = "siammakro.cloud"
 *   → *.siammakro.cloud = same-domain
 *   → anything else = external
 */
function classifyUrl(
  url: string,
  config?: ClassifyUrlConfig
): { classification: UrlClassification; parsed: URL | null } {
  const appUrl = config?.appUrl ?? process.env.NEXT_PUBLIC_APP_URL ?? '';

  // 1. Parse the incoming URL
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Not a valid absolute URL — treat as internal relative path
    return { classification: 'internal', parsed: null };
  }

  // 2. Dangerous schemes → block by treating as internal (router.push is safe)
  if (BLOCKED_SCHEMES.has(parsed.protocol)) {
    return { classification: 'internal', parsed: null };
  }

  // 3. Native OS schemes (tel:, mailto:, sms:, …) → passthrough
  if (PASSTHROUGH_SCHEMES.has(parsed.protocol)) {
    return { classification: 'passthrough', parsed };
  }

  // 4. Parse the app's hostname
  let appHostname: string;
  try {
    appHostname = new URL(appUrl).hostname.toLowerCase();
  } catch {
    // App URL not configured — treat everything as internal
    return { classification: 'internal', parsed };
  }

  const incomingHostname = parsed.hostname.toLowerCase();

  // 5. Exact hostname match (including subdomain) → internal
  if (incomingHostname === appHostname) {
    return { classification: 'internal', parsed };
  }

  // 6. Same base domain, different subdomain → same-domain
  //    Extracts registrable domain from appUrl to handle multi-level subdomains.
  //    e.g. app = storefront.my-dev.siammakro.cloud
  //         registrable = siammakro.cloud
  //         url = api.other-env.siammakro.cloud → same-domain
  const baseDomain = extractRegistrableDomain(appHostname);
  if (baseDomain && isSubdomainOf(incomingHostname, baseDomain)) {
    return { classification: 'same-domain', parsed };
  }

  // 7. Completely different domain → external
  return { classification: 'external', parsed };
}

/**
 * Extracts the registrable domain (eTLD+1) from a hostname.
 * Handles both single-part TLDs (.com, .cloud) and two-part TLDs (.com.my, .co.uk).
 *
 * Examples:
 *   "storefront.my-dev.siammakro.cloud"  → "siammakro.cloud"
 *   "storefront.shopping.makro.com.my"   → "makro.com.my"
 *   "www.makro.co.th"                    → "makro.co.th"
 *   "api.example.com"                    → "example.com"
 *   "siammakro.cloud"                    → "siammakro.cloud"  (already at root)
 *   "localhost"                          → ""  (no TLD, cannot determine)
 */
function extractRegistrableDomain(hostname: string): string {
  const parts = hostname.split('.');
  if (parts.length < 2) return '';

  // Check if the second-to-last label is a known SLD (e.g. "com" in "com.my")
  const secondToLast = parts[parts.length - 2];
  const isTwoPartTld = SECOND_LEVEL_TLDS.has(secondToLast);

  // registrable domain = one label + TLD
  // For single-part TLD (.cloud): last 2 labels → "siammakro.cloud"
  // For two-part TLD (.com.my):   last 3 labels → "makro.com.my"
  const registrableLength = isTwoPartTld ? 3 : 2;

  // If hostname has fewer or equal parts than the registrable length,
  // it's already at the root level (e.g. "siammakro.cloud" or "makro.com.my")
  if (parts.length <= registrableLength) return hostname;

  return parts.slice(-registrableLength).join('.');
}

/**
 * Returns true if hostname equals baseDomain or is a subdomain of it.
 * e.g. isSubdomainOf("api.makro.com.my", "makro.com.my") → true
 *      isSubdomainOf("evil-makro.com.my", "makro.com.my") → false
 */
function isSubdomainOf(hostname: string, baseDomain: string): boolean {
  return hostname === baseDomain || hostname.endsWith(`.${baseDomain}`);
}

// ============================================
// Internal Helpers
// ============================================

/**
 * Safely posts a message to React Native WebView.
 * Returns true if message was sent, false otherwise.
 */
export const sendMessageToRN = (message: OutgoingRNMessage): boolean => {
  if (typeof window === 'undefined') return false;
  if (!hasRNWebViewMessaging()) return false;

  try {
    window.ReactNativeWebView!.postMessage(JSON.stringify(message));
    return true;
  } catch {
    return false;
  }
};

// ============================================
// Context Types
// ============================================

type LayoutContextType = {
  deviceType: DeviceType;
  isInWebView: boolean | null;
  postMessageToRN: (message: OutgoingRNMessage) => void;
  subscribeToRNMessages: (handler: RNMessageHandler) => () => void;
  disableMinHeight: boolean;
  setDisableMinHeight: (disable: boolean) => void;
};

const LayoutContext = createContext<LayoutContextType | null>(null);

// ============================================
// Provider Component
// ============================================

export const LayoutProvider: React.FC<{
  children: React.ReactNode;
  deviceType: DeviceType;
  isInWebView: boolean | null;
}> = ({ children, deviceType, isInWebView }) => {
  const router = useRouter();

  const rnStateRef = useRef({
    isMounted: false,
    listenersAttached: false,
  });

  const messageHandlersRef = useRef<Set<RNMessageHandler>>(new Set());

  const routerRef = useRef(router);
  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  /**
   * Handle incoming URL message from RN WebView.
   *
   * 2.1: Internal (exact hostname match)    → navigate with Next.js router, no event back to RN
   * 2.2: Same-domain (different subdomain)  → navigate with window.location.href, no event back to RN
   * 2.3: External (different domain)        → send OPEN_IN_APP_BROWSER to RN
   */
  const handleUrlMessage = useCallback((url: string) => {
    const { classification, parsed } = classifyUrl(url);
    switch (classification) {
      case 'internal': {
        if (parsed) {
          // Absolute URL with matching hostname → extract path for client-side navigation
          routerRef.current.push(parsed.pathname + parsed.search + parsed.hash);
        } else {
          // Relative path or unparseable URL → navigate directly
          routerRef.current.push(url);
        }
        break;
      }
      case 'same-domain': {
        // Same base domain, different subdomain → navigate directly, no RN event
        window.location.href = url;
        break;
      }
      case 'external': {
        // Different domain → tell RN to open in-app browser
        sendMessageToRN({
          type: RNMessageType.OPEN_IN_APP_BROWSER,
          data: { value: url },
        });
        break;
      }
      case 'passthrough': {
        // Native scheme (tel:, mailto:, sms:) → let browser / OS handle it
        window.location.href = url;
        break;
      }
    }
  }, []);

  /**
   * Handle incoming RN WebView messages (from window "message" event).
   */
  const handleRNMessage = useCallback(
    (event: MessageEvent) => {
      if (!rnStateRef.current.isMounted) return;

      const messageData = extractMessageData(event as RNMessageEvent);
      if (!messageData) return;

      try {
        const parsed = JSON.parse(messageData);
        if (!isValidRNMessage(parsed)) return;

        // Handle URL messages
        handleUrlMessage(parsed.data.value);

        // Notify subscribers
        messageHandlersRef.current.forEach((handler) => {
          try {
            handler(parsed);
          } catch {
            // Silently catch handler errors
          }
        });
      } catch {
        // Invalid JSON, ignore
      }
    },
    [handleUrlMessage]
  );

  /**
   * Post message to React Native WebView.
   * Exposed via context for components to send outgoing messages.
   */
  const postMessageToRN = useCallback((message: OutgoingRNMessage) => {
    if (!rnStateRef.current.isMounted) return;
    sendMessageToRN(message);
  }, []);

  /**
   * Subscribe to incoming RN WebView messages.
   * Returns an unsubscribe function.
   */
  const subscribeToRNMessages = useCallback(
    (handler: RNMessageHandler): (() => void) => {
      if (typeof window === 'undefined' || !hasRNWebViewMessaging()) {
        return () => {};
      }

      messageHandlersRef.current.add(handler);
      return () => {
        messageHandlersRef.current.delete(handler);
      };
    },
    []
  );

  /**
   * Check if RN WebView is available (client-side ground truth).
   */
  const isWebViewAvailable = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;
    if (hasRNWebViewMessaging()) return true;
    return !!isInWebView;
  }, [isInWebView]);

  // ============================================
  // Effect: Initialize RN WebView communication
  // ============================================
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const state = rnStateRef.current;
    const currentMessageHandlersRef = messageHandlersRef.current;
    state.isMounted = true;

    if (false) {
      return () => {
        state.isMounted = false;
      };
    }

    // Attach event listeners only once
    if (!state.listenersAttached) {
      window.addEventListener('message', handleRNMessage);
      document.addEventListener('message', handleRNMessage as EventListener);
      state.listenersAttached = true;
    }

    return () => {
      state.isMounted = false;

      if (state.listenersAttached) {
        window.removeEventListener('message', handleRNMessage);
        document.removeEventListener(
          'message',
          handleRNMessage as EventListener
        );
        currentMessageHandlersRef.clear();
        state.listenersAttached = false;
      }
    };
  }, [isWebViewAvailable, handleRNMessage]);

  // ============================================
  // Effect: Intercept external link clicks in WebView
  // ============================================
  // When running inside a RN WebView, capture all <a> clicks.
  // If the href points to an external domain, prevent the default
  // navigation and dispatch OPEN_IN_APP_BROWSER to the native app.
  // ============================================
  useEffect(() => {
    if (typeof document === 'undefined') return;
    //if (!isWebViewAvailable()) return;

    const handleLinkClick = (e: MouseEvent) => {
      // Walk up from the click target to find the nearest <a>
      const anchor = (e.target as HTMLElement)?.closest?.('a');
      if (!anchor) return;

      // Use the DOM .href property (fully resolved absolute URL)
      // instead of getAttribute("href") which returns the raw attribute
      // and would miss protocol-relative URLs like "//external.com/path"
      const href = anchor.href;
      if (!href) return;

      const { classification } = classifyUrl(href);

      if (classification === 'external') {
        // Block the browser from navigating
        e.preventDefault();
        e.stopPropagation();

        // Tell RN to open it in the in-app browser
        sendMessageToRN({
          type: RNMessageType.OPEN_IN_APP_BROWSER,
          data: { value: href },
        });
      }
      // "passthrough" (tel:, mailto:, sms:) — do nothing, let browser/OS handle
      // "internal" / "same-domain" — do nothing, let normal navigation proceed
    };

    // Use capture phase so we intercept before any other click handlers
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [isWebViewAvailable]);

  const [disableMinHeight, setDisableMinHeight] = useState(false);

  // ============================================
  // Context value (memoized)
  // ============================================
  const contextValue = useMemo<LayoutContextType>(
    () => ({
      deviceType,
      isInWebView: isInWebView || isWebViewAvailable(),
      postMessageToRN,
      subscribeToRNMessages,
      disableMinHeight,
      setDisableMinHeight,
    }),
    [
      deviceType,
      isWebViewAvailable,
      postMessageToRN,
      subscribeToRNMessages,
      disableMinHeight,
      isInWebView,
    ]
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      <SnackbarProvider>{children}</SnackbarProvider>
    </LayoutContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
